import { GetChatArgs } from '@/types/chat.types';
import { ApiResponse, PaginatedResponse } from '@/types/Common';
import {
  Chat,
  ChatMedia,
  GetRequestsArgs,
  Request,
  GetRequestQuestionsResponse,
  RequestStatus,
  RequestComment,
} from '@/types/request';
import { RequestStatsFilters } from '@/types/statistics.types';
import api, { getUploadedPercentage } from '@/utils/axios/api';

export const getRequests = async (queryArgs: GetRequestsArgs) => {
  const response = await api.get<ApiResponse<PaginatedResponse<Request>>>(
    '/requests',
    {
      params: queryArgs,
    },
  );
  return response.data.data;
};

export const changeRequestStatus = async (
  id: string,
  data: {
    status: string;
    transferReason?: string;
    newOrganizationId?: string;
    newServiceId?: string;
    comments?: string;
  },
) => {
  const endpoint = `/requests/${id}/change-status`;
  let payload: any = { status: data.status };

  if (data.status === RequestStatus.Referred) {
    payload = {
      ...payload,
      transferReason: data.transferReason,
      newOrganizationId: data.newOrganizationId,
      serviceId: data.newServiceId, // Changed from newServiceId to serviceId
    };
  }

  if (
    ['InProgress', 'Pending', 'Resolved'].includes(data.status) &&
    data.comments !== undefined
  ) {
    payload = {
      ...payload,
      comments: data.comments,
    };
  }

  const response = await api.put<ApiResponse<Request>>(endpoint, payload);
  return response.data;
};

export const uploadChatMedia = async (
  formData: FormData,
  onUploadProgress: (percentage: number, isUploading: boolean) => void,
) => {
  onUploadProgress(0, true);
  const response = await api.post<ApiResponse<ChatMedia[]>>(
    '/chats/upload-media',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent_1) => {
        const percentage_1 = getUploadedPercentage(progressEvent_1);
        onUploadProgress(percentage_1, true);
      },
    },
  );
  onUploadProgress(100, false);
  return response;
};

export const getChatHistory = async (queryArgs: GetChatArgs) => {
  const res = await api.get<ApiResponse<PaginatedResponse<Chat>>>(
    '/chats/history',
    {
      params: queryArgs,
    },
  );

  return res.data.data;
};

export const getRequestsCountPerStatus = async (
  params: RequestStatsFilters,
) => {
  const res = await api.get<
    ApiResponse<
      {
        [keys in RequestStatus]: number;
      } & {
        All: number;
      }
    >
  >('/stats/dashboard/requests-per-status', {
    params: params,
  });

  return res.data.data;
};

export const getRequestQuestions = async (serviceProviderId: string) => {
  const response = await api.get<GetRequestQuestionsResponse>(
    '/requests/questions',
    {
      params: {
        serviceProviderId,
      },
    },
  );
  return response.data.data;
};

export const getRequestById = async (id: string) => {
  const response = await api.get<ApiResponse<Request>>(`/requests/${id}`);
  return response.data.data;
};

export const getRequestComments = async (requestId: string) => {
  const response = await api.get<ApiResponse<RequestComment[]>>(
    `/requests/${requestId}/comments`,
  );
  return response.data.data;
};

export const addRequestComment = async (requestId: string, comment: string) => {
  const response = await api.post<ApiResponse<RequestComment>>(
    `/requests/${requestId}/comments`,
    {
      comment,
    },
  );
  return response.data.data;
};
